import React from "react"

const HASH = "#modal"

/**
 * Makes the browser's Back button close a dialog.
 *
 * Opening pushes a `#modal` history entry, so Back — and the Android system
 * back, which is the case that actually matters — pops it and lands on the same
 * page with the dialog dismissed, instead of navigating away from the app
 * entirely with the dialog still notionally open.
 *
 * This is `Modal.jsx`'s trick, which every legacy dialog gets for free, lifted
 * out for the shadcn dialogs that no longer render through it. It is NOT a
 * literal port: `Modal.jsx` compares the hash in `componentDidUpdate`, so it
 * only notices a press on the next render, and a press while nothing else was
 * changing did nothing at all. This listens for `hashchange`.
 *
 * `onClose` must do the caller's real teardown — unmount the dialog, clear the
 * modal stack — not merely hide it. It is read through a ref so the effect can
 * run once for the life of the dialog: callers pass inline arrows, and keying
 * the effect on one would tear down and rebuild the history entry on every
 * render.
 *
 * Pass `false` for a dialog that shouldn't claim a history entry — a nested one,
 * or one that has no dismissal of its own.
 */
export function useBackToClose(onClose: () => void, enabled = true) {
  const closeRef = React.useRef(onClose)
  closeRef.current = onClose

  React.useEffect(() => {
    if (!enabled) return

    /*
     * Only push when the hash isn't already ours. A legacy modal open
     * underneath has set the same one, and a second entry would cost two back
     * presses to dismiss one dialog.
     */
    const pushed = window.location.hash !== HASH

    if (pushed) {
      window.location.hash = "modal"
    }

    /*
     * Judged by the URL the event moved to, not by where the page is now. The
     * push above fires a hashchange of its own, delivered later, and a router
     * redirect in between (replaceState drops the hash without firing one)
     * made that event read as Back and shut a dialog that had just opened.
     */
    const onHashChange = (event: HashChangeEvent) => {
      if (new URL(event.newURL).hash !== HASH) {
        closeRef.current()
      }
    }

    window.addEventListener("hashchange", onHashChange)

    return () => {
      window.removeEventListener("hashchange", onHashChange)

      /*
       * Closing any other way leaves `#modal` in the URL, so it is stripped in
       * place — replaceState, not a back navigation: the dialog is already gone
       * by now, and going back would move the user off the page. Same as
       * Modal.jsx's own unmount.
       */
      if (pushed && window.location.hash === HASH) {
        window.history.replaceState({}, "", window.location.href.split("#")[0])
      }
    }
  }, [enabled])
}
